import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import PageHeader from "../../../components/PageHeader";
import Modal from "../../../components/Modal";
import {
  controleProducaoService,
  UndMedida,
} from "../../../services/controleProducaoService";

type FormState = {
  id: number | null;
  nome: string;
  ativo: boolean;
};

const EMPTY_FORM: FormState = {
  id: null,
  nome: "",
  ativo: true,
};

const ControleProducaoUndMedidasPage: React.FC = () => {
  const [items, setItems] = useState<UndMedida[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const loadUnds = async () => {
    setLoading(true);
    try {
      setItems(await controleProducaoService.listUndMedidas());
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Erro ao carregar unidades de medida.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUnds();
  }, []);

  const modalTitle = useMemo(
    () =>
      form.id ? "Editar unidade de medida" : "Nova unidade de medida",
    [form.id]
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nome = form.nome.trim();
    if (!nome) {
      toast.warning("Informe o nome da unidade.");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await controleProducaoService.updateUndMedida(form.id, {
          nome,
          ativo: form.ativo,
        });
        toast.success("Unidade atualizada.");
      } else {
        await controleProducaoService.createUndMedida({
          nome,
          ativo: form.ativo,
        });
        toast.success("Unidade criada.");
      }
      setModalOpen(false);
      setForm(EMPTY_FORM);
      await loadUnds();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao salvar unidade.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <PageHeader
          title="Unidades de Medida"
          description="Cadastro das unidades usadas na base semanal e nas fichas diárias."
        />
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <button
            type="button"
            className="h-[38px] w-full rounded-md bg-primary px-4 text-sm font-semibold text-white sm:w-auto"
            onClick={() => {
              setForm(EMPTY_FORM);
              setModalOpen(true);
            }}
          >
            Nova Unidade
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Nome
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Status
              </th>
              <th className="px-4 py-3 text-right font-semibold text-neutral-600">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-neutral-200">
                <td className="px-4 py-3 font-medium text-neutral-800">
                  {item.nome}
                </td>
                <td className="px-4 py-3">
                  {item.ativo ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                      Ativo
                    </span>
                  ) : (
                    <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                      Inativo
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
                    onClick={() => {
                      setForm({
                        id: item.id,
                        nome: item.nome,
                        ativo: item.ativo,
                      });
                      setModalOpen(true);
                    }}
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-neutral-500"
                  colSpan={3}
                >
                  Nenhuma unidade cadastrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <p className="mt-3 text-sm text-neutral-500">Carregando unidades...</p>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
      >
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block text-sm font-medium text-neutral-700">
            Nome (ex.: kg, un, lata)
            <input
              value={form.nome}
              maxLength={30}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, nome: e.target.value }))
              }
              className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
            />
          </label>
          {form.id != null && (
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={form.ativo}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, ativo: e.target.checked }))
                }
              />
              Unidade ativa
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </Modal>
    </main>
  );
};

export default ControleProducaoUndMedidasPage;
